import { getSiteUrl } from '../pages/Landing/landingUtils'

export type BlogShareChannel =
  | 'copy'
  | 'telegram'
  | 'vk'
  | 'email'
  | 'native'

/** Default Open Graph image for blog posts (absolute URL). */
export const BLOG_DEFAULT_OG_IMAGE_PATH = '/landing/1.png'

export function getBlogDefaultOgImageUrl(): string {
  return getSiteUrl(BLOG_DEFAULT_OG_IMAGE_PATH)
}

export function buildBlogShareUrl(slug: string, channel: BlogShareChannel | string): string {
  const base = getSiteUrl(`/blog/${slug}`)
  const params = new URLSearchParams({
    utm_source: channel,
    utm_medium: 'blog_share',
    utm_campaign: slug,
  })
  return `${base}?${params.toString()}`
}

export function buildBlogShareMessage(title: string, excerpt: string | null | undefined, url: string): string {
  const summary = excerpt?.trim() || title
  return `«${title}» — ${summary}\n${url}`
}

export function canUseNativeShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
}

export async function copyBlogShareLink(urlOrText: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(urlOrText)
      return true
    }
  } catch {
    /* fallback below */
  }

  try {
    const ta = document.createElement('textarea')
    ta.value = urlOrText
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

export function openShareWindow(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer')
}

export function shareToTelegram(title: string, excerpt: string | null | undefined, slug: string): void {
  const url = buildBlogShareUrl(slug, 'telegram')
  const text = buildBlogShareMessage(title, excerpt, url)
  openShareWindow(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`)
}

export function shareToVk(
  title: string,
  excerpt: string | null | undefined,
  slug: string,
  imageUrl?: string | null,
): void {
  const url = buildBlogShareUrl(slug, 'vk')
  const params = new URLSearchParams({
    url,
    title,
  })
  const description = excerpt?.trim()
  if (description) {
    params.set('description', description)
  }
  if (imageUrl) {
    params.set('image', imageUrl)
  }
  openShareWindow(`https://vk.com/share.php?${params.toString()}`)
}

export function shareByEmail(title: string, excerpt: string | null | undefined, slug: string): void {
  const url = buildBlogShareUrl(slug, 'email')
  const body = buildBlogShareMessage(title, excerpt, url)
  window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`
}

export async function shareNative(
  title: string,
  excerpt: string | null | undefined,
  slug: string,
): Promise<boolean> {
  if (!canUseNativeShare()) return false
  const url = buildBlogShareUrl(slug, 'native')
  const text = excerpt?.trim() || title
  try {
    await navigator.share({ title, text, url })
    return true
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return false
    return false
  }
}
